import React, { useEffect } from "react";
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Typography,
  Icon,
  Avatar,
  alpha,
  useTheme,
  Divider,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { LogoutDialog } from "../LogoutDialog/LogoutDialog";
import { usePOSService } from "../../hooks/usePOSService";
import { SessionClose } from "../SessionManagement/SessionClose/SessionClose";
import { toast } from "react-toastify";
import { SessionResponse } from "../../types/pos.types";
import posLogo from "../../../../assets/logo/pos-logo-text.png";
import { useAuth } from "../../../auth/hooks/useAuth";
import { SupervisorPinModal } from "../SuperuserPinModal/SuperuserPinModal";
import { useUserRole } from "../../hooks/useUserRole";
import { hasPermission } from "../../../auth/utils/permission.utils";

const DRAWER_WIDTH = 280;

interface POSLayoutProps {
  children: React.ReactNode;
}

export const POSLayout: React.FC<POSLayoutProps> = ({ children }) => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const { user, logout } = useAuth();
  const userRole = useUserRole(); // Returns 'super_admin', 'store_admin', 'cashier', 'waiter', etc.
  console.log(userRole);
  const { endSession, sessionId, posService } = usePOSService();

  // 🔥 Auto-redirect if user is waiter
  // useEffect(() => {
  //   if (userRole === "waiter" && location.pathname !== "/pos/waiterDashboard") {
  //     navigate("/pos/waiterDashboard", { replace: true });
  //   }
  // }, [userRole, navigate, location.pathname]);

  const [logoutDialogOpen, setLogoutDialogOpen] = React.useState(false);
  const [endSessionDialogOpen, setEndSessionDialogOpen] = React.useState(false);
  const [isLoggingOut, setIsLoggingOut] = React.useState(false);
  const [isEndingSession, setIsEndingSession] = React.useState(false);
  const [sessionData, setSessionData] = React.useState<SessionResponse | null>(
    null
  );
  const [showPinModal, setShowPinModal] = React.useState(false);

  const handlePinSuccess = () => {
    setShowPinModal(false);
    setEndSessionDialogOpen(true);
  };

  const fetchSessionData = () => {
    if (sessionId) {
      posService
        .getSessionDetails(sessionId)
        .then((response) => {
          if (response.success) {
            setSessionData(response.data);
          }
        })
        .catch((error) => {
          console.error("Error fetching session data:", error);
        });
    }
  };

  React.useEffect(fetchSessionData, [sessionId]);

  const handleEndSession = async (data: {
    session_id: string;
    final_denominations: {
      bills: Array<{ value: number; count: number }>;
      coins: Array<{ value: number; count: number }>;
    };
  }) => {
    try {
      setIsEndingSession(true);
      await endSession(data);
      toast.success("Session ended successfully");
    } catch (error) {
      console.error("Error ending session:", error);
      toast.error("Failed to end session");
    } finally {
      setIsEndingSession(false);
      setEndSessionDialogOpen(false);
    }
  };

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      if (sessionId) {
        await logout();
      }
      navigate("/auth/login");
    } catch (error) {
      console.error("Error during logout:", error);
    } finally {
      setIsLoggingOut(false);
      setLogoutDialogOpen(false);
    }
  };

  // Define all possible routes
  const ALL_MENU_ITEMS = [
    // Cashier & Admin
    { label: "Menu", icon: "restaurant_menu", path: "/pos/menu", roles: ["supervisor", "cashier"] },
    { label: "Orders", icon: "receipt_long", path: "/pos/orders", roles: ["supervisor", "cashier"] },

    // Admin only
    { label: "Dashboard", icon: "dashboard", path: "/pos/dashboard", roles: ["supervisor"] },
    { label: "Daybook", icon: "book", path: "/pos/daybook", roles: ["supervisor"] },

    // Waiter only
    { label: "Waiter Dashboard", icon: "room_service", path: "/pos/waiter/dashboard", roles: ["waiter"] },
  ];
  // Filter based on user role
  const getMenuItemsForRole = () => {
    if (userRole === "supervisor") {
      // Admins see all except waiter dashboard
      return ALL_MENU_ITEMS.filter(
        item => item.roles.includes("supervisor") && item.path !== "/pos/waiter/dashboard"
      );
    }

    if (userRole === "cashier") {
      // Cashier sees only cashier items (excluding dashboard/daybook)
      return ALL_MENU_ITEMS.filter(
        item =>
          item.roles.includes("cashier") &&
          item.path !== "/pos/dashboard" &&
          item.path !== "/pos/daybook"
      );
    }

    if (userRole === "waiter") {
      // Waiter only sees waiter dashboard
      return ALL_MENU_ITEMS.filter(item => item.path === "/pos/waiter/dashboard");
    }

    return [];
  };

  const MENU_ITEMS = getMenuItemsForRole();

  const handleEndSessionClick = () => {
    setShowPinModal(true);
  };

  return (
    <Box sx={{ display: "flex", height: "100vh" }}>
      {/* Sidebar */}
      <Drawer
        variant="permanent"
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: DRAWER_WIDTH,
            boxSizing: "border-box",
            backgroundColor: alpha(theme.palette.background.paper, 0.8),
            backdropFilter: "blur(8px)",
            borderRight: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            p: 3,
          }}
        >
          {/* Logo */}
          <Box sx={{ mb: 4 }}>
            <img src={posLogo} alt="POS Logo" style={{ width: "100%", height: "auto" }} />
          </Box>

          {/* User Profile */}
          <Box sx={{ mb: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                p: 1,
                borderRadius: 2,
                "&:hover": {
                  backgroundColor: alpha("#1E8078", 0.1),
                },
              }}
            >
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  backgroundColor: alpha("#1E8078", 0.1),
                  color: "#1E8078",
                }}
              >
                {user?.first_name?.[0]}
                {user?.last_name?.[0]}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {user?.first_name} {user?.last_name}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {userRole === "waiter"
                    ? "Waiter"
                    : userRole === "cashier"
                      ? "Cashier"
                      : "Admin"}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Navigation */}
          <List sx={{ flex: 1 }}>
            {MENU_ITEMS.map((item) => (
              <ListItem key={item.path} disablePadding>
                <ListItemButton
                  onClick={() => navigate(item.path)}
                  selected={location.pathname === item.path}
                  sx={{
                    mb: 1,
                    borderRadius: 2,
                    "&.Mui-selected": {
                      backgroundColor: alpha("#1E8078", 0.1),
                      "&:hover": {
                        backgroundColor: alpha("#1E8078", 0.15),
                      },
                    },
                  }}
                >
                  <ListItemIcon>
                    <Icon
                      sx={{
                        color:
                          location.pathname === item.path
                            ? "#1E8078"
                            : "text.secondary",
                      }}
                    >
                      {item.icon}
                    </Icon>
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      sx: {
                        fontWeight: location.pathname === item.path ? 600 : 400,
                        color:
                          location.pathname === item.path
                            ? "#1E8078"
                            : "text.primary",
                      },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>

          {/* Session and Logout Buttons */}
          <List>
            {sessionId && !(userRole === "waiter") && (
              <ListItem disablePadding sx={{ mb: 1 }}>
                <ListItemButton
                  onClick={handleEndSessionClick}
                  disabled={!hasPermission("pos.session.end", user?.newPermissions, user?.assignment_details?.id || "", user?.is_store_owner || false)}
                  sx={{
                    borderRadius: 2,
                    color: theme.palette.warning.main,
                    "&:hover": {
                      backgroundColor: alpha(theme.palette.warning.main, 0.1),
                    },
                  }}
                >
                  <ListItemIcon>
                    <Icon sx={{ color: theme.palette.warning.main }}>timer_off</Icon>
                  </ListItemIcon>
                  <ListItemText primary="End Session" />
                </ListItemButton>
              </ListItem>
            )}

            <ListItem disablePadding>
              <ListItemButton
                onClick={() => setLogoutDialogOpen(true)}
                sx={{
                  borderRadius: 2,
                  color: theme.palette.error.main,
                  "&:hover": {
                    backgroundColor: alpha(
                      theme.palette.error.main,
                      0.1
                    ),
                  },
                }}
              >
                <ListItemIcon>
                  <Icon sx={{ color: theme.palette.error.main }}>
                    logout
                  </Icon>
                </ListItemIcon>
                <ListItemText primary="Logout" />
              </ListItemButton>
            </ListItem>
          </List>

          {/* Copyright */}
          <Typography
            variant="caption"
            sx={{
              mt: 2,
              textAlign: "center",
              color: "text.disabled",
              fontWeight: 500,
            }}
          >
            © 2025 VenFlows
          </Typography>
        </Box>
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          height: "100vh",
          overflow: "auto",
          bgcolor: "background.default",
        }}
      >
        {children}
      </Box>

      {/* Modals */}
      <SupervisorPinModal
        open={showPinModal}
        action="end_session"
        reasonLabel="Reason for Ending Session"
        onClose={() => setShowPinModal(false)}
        onSuccess={handlePinSuccess}
      />

      {sessionData && (
        <SessionClose
          open={endSessionDialogOpen}
          onClose={() => setEndSessionDialogOpen(false)}
          onSubmit={handleEndSession}
          sessionData={sessionData}
        />
      )}

      <LogoutDialog
        open={logoutDialogOpen}
        onClose={() => setLogoutDialogOpen(false)}
        onConfirm={handleLogout}
        isLoading={isLoggingOut}
      />
    </Box>
  );
};